(function () {
  const PAGE_SIZE = 24;
  const OPTION_LIMIT = 80;
  const TYPE_OPTION_STEP = 80;
  const DEFAULT_SORT = "updated_desc";
  const NEW_BADGE_LIFETIME_MS = 24 * 60 * 60 * 1000;

  let FILTERS = [
    {
      key: "stock",
      label: "Lagerbestand",
      fixed: [
        ["available", "Verfügbar"],
        ["preorder", "Vorbesteller"],
        ["soldout", "Ausverkauft"],
      ],
    },
    { key: "type", label: "Produkttyp", prop: "type" },
    { key: "origin", label: "Herkunft", prop: "origin" },
    { key: "size", label: "Größe", prop: "size" },
    { key: "scale", label: "Massstab", prop: "scale" },
    { key: "vendor", label: "Marke/Hersteller", prop: "vendor" },
    {
      key: "price",
      label: "Preis, EUR",
      range: true,
    },
    { key: "sale", label: "Angebote", fixed: [["sale", "Reduziert"]] },
    { key: "platform", label: "Plattform", prop: "platform" },
  ];

  let DROPDOWN_FILTERS = FILTERS.filter((filter) => filter.key !== "stock");

  const SORTS = [
    ["updated_desc", "Zuletzt aktualisiert"],
    ["created_desc", "Zuletzt hinzugefuegt"],
    ["best_selling", "Meist verkauft"],
    ["price_asc", "Preis aufsteigend"],
    ["price_desc", "Preis absteigend"],
    ["title_asc", "Titel A-Z"],
    ["title_desc", "Titel Z-A"],
  ];

  const currency = new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  });

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function normalizeText(value) {
    return String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  function compactSearchText(value) {
    return normalizeText(value).replace(/\s+/g, "");
  }

  function publicSearchValue(value) {
    return String(value || "").replace(/\bsec;;.*$/i, "").trim();
  }

  function publicFilterValue(value) {
    const text = publicSearchValue(value);
    if (!text || text.trim().startsWith("&")) return "";
    return text.trim();
  }

  function blockedValueKey(value) {
    return normalizeText(publicFilterValue(value));
  }

  function isBlockedFilterValue(blocklist, filterKey, value) {
    const key = blockedValueKey(value);
    if (!key) return true;
    const values = blocklist && blocklist[filterKey];
    return values instanceof Set && values.has(key);
  }

  function canonicalFilterValue(blocklist, filterKey, value) {
    const clean = publicFilterValue(value);
    if (!clean) return "";
    const aliases = blocklist && blocklist.__aliases && blocklist.__aliases[filterKey];
    const mapped = aliases instanceof Map ? aliases.get(blockedValueKey(clean)) : "";
    return mapped || clean;
  }

  function normalizeCustomFilter(item) {
    if (!item || item.enabled === false) return null;
    const key = publicFilterValue(item.key);
    const label = publicFilterValue(item.label);
    if (!key || !label) return null;
    return {
      key: `custom_${key}`,
      label,
      customKey: key,
    };
  }

  function applyCustomFilters(customFilters) {
    const baseKeys = new Set(FILTERS.map((filter) => filter.key));
    const extra = (Array.isArray(customFilters) ? customFilters : [])
      .map(normalizeCustomFilter)
      .filter((filter) => filter && !baseKeys.has(filter.key));
    if (!extra.length) return;
    FILTERS = FILTERS.concat(extra);
    DROPDOWN_FILTERS = FILTERS.filter((filter) => filter.key !== "stock");
  }

  function platformAliases(value) {
    const text = normalizeText(value);
    const aliases = [];
    if (!text) return aliases;
    if (/\b(ps4|playstation 4|play station 4|sony playstation 4)\b/.test(text)) aliases.push("ps4", "playstation4", "playstation 4", "sony playstation 4");
    if (/\b(ps5|playstation 5|play station 5|sony playstation 5)\b/.test(text)) aliases.push("ps5", "playstation5", "playstation 5", "sony playstation 5");
    if (/\b(switch 2|nintendo switch 2)\b/.test(text)) aliases.push("switch2", "switch 2", "nintendo switch 2");
    if (/\b(switch|nintendo switch)\b/.test(text)) aliases.push("switch", "nintendo switch");
    if (/\b(xbox one|microsoft xbox one)\b/.test(text)) aliases.push("xboxone", "xbox one", "microsoft xbox one");
    if (/\b(xbox x|series x|xbox series x|microsoft xbox x)\b/.test(text)) aliases.push("xboxx", "seriesx", "xbox series x", "microsoft xbox x");
    if (/\b(pc|windows)\b/.test(text)) aliases.push("pc", "windows");
    return aliases;
  }

  function searchTokenAliases(token) {
    const text = normalizeText(token);
    const compact = compactSearchText(text);
    if (!text) return [];
    if (["tcg", "karten", "karte", "cards", "card"].includes(compact)) {
      return [
        "tcg",
        "spielkarten",
        "sammelkarten",
        "einzelkarten",
        "trading card",
        "trading cards",
        "card game",
        "booster",
        "display",
        "deck",
        "starter deck",
        "starterdeck",
      ];
    }
    if (["figur", "figuren", "figure", "figures", "statue", "statuen"].includes(compact)) {
      return [
        "figur",
        "figuren",
        "figure",
        "figures",
        "statue",
        "statuen",
        "complete figure",
        "actionfigur",
        "actionfiguren",
      ];
    }
    return [];
  }

  function searchTokens(query) {
    const normalized = normalizeText(query);
    const expanded = [normalized].concat(platformAliases(normalized)).join(" ");
    return normalizeText(expanded).split(/\s+/).filter(Boolean);
  }

  function searchHaystack(product) {
    const parts = [
      product.title,
      product.handle,
      product.ean,
      publicFilterValue(product.vendor),
      publicFilterValue(product.origin),
      ...(Array.isArray(product.collections) ? product.collections : [product.collections]).map(publicFilterValue),
      ...(Array.isArray(product.type) ? product.type : [product.type]).map(publicFilterValue),
      ...(Array.isArray(product.size) ? product.size : [product.size]).map(publicFilterValue),
      ...(Array.isArray(product.scale) ? product.scale : [product.scale]).map(publicFilterValue),
      ...(Array.isArray(product.platform) ? product.platform : [product.platform]).map(publicFilterValue),
      ...Object.values(product.custom || {}).flat().map(publicFilterValue),
      ...(Array.isArray(product.search_values) ? product.search_values : []).map(publicFilterValue),
    ];
    if (Array.isArray(product.variants)) {
      for (const variant of product.variants) {
        parts.push(variant.title, variant.sku, publicSearchValue(variant.barcode), variant.option1, variant.option2, variant.option3);
      }
    }
    const platforms = Array.isArray(product.platform) ? product.platform : [product.platform];
    for (const platform of platforms) {
      parts.push(...platformAliases(platform));
    }
    return normalizeText(parts.filter(Boolean).join(" "));
  }

  // PERFORMANCE (26.07.): Suchtext, Titel und Hersteller werden pro Produkt
  // nur EINMAL normalisiert und dann am Produkt zwischengespeichert. Vorher
  // wurde das bei JEDEM Tastendruck fuer ~23.000 Produkte neu berechnet -
  // deshalb hing die Filter-Suche.
  function ensureSearchCache(product) {
    if (product._hay === undefined) {
      product._hay = searchHaystack(product);
      product._hayCompact = compactSearchText(product._hay);
      product._hayWords = new Set(product._hay.split(/\s+/).filter(Boolean));
      product._tnorm = normalizeText(product.title);
      product._tcompact = compactSearchText(product.title);
      product._vnorm = normalizeText(product.vendor);
      product._vcompact = compactSearchText(product.vendor);
      product._onorm = normalizeText(product.origin);
      product._ocompact = compactSearchText(product.origin);
      product._typenorm = normalizeText((Array.isArray(product.type) ? product.type : [product.type]).filter(Boolean).join(" "));
      product._typecompact = compactSearchText(product._typenorm);
      product._platformnorm = normalizeText((Array.isArray(product.platform) ? product.platform : [product.platform]).filter(Boolean).join(" "));
      product._platformcompact = compactSearchText(product._platformnorm);
      product._eanNorm = normalizeText(product.ean);
    }
    return product;
  }

  function matchesSearchToken(product, token) {
    const alternatives = [token].concat(searchTokenAliases(token));
    if (/^\d{1,3}$/.test(token)) {
      return product._hayWords && product._hayWords.has(token);
    }
    return alternatives.some((item) => {
      const normalized = normalizeText(item);
      const compactToken = compactSearchText(item);
      return (normalized && product._hay.includes(normalized)) || (compactToken.length >= 4 && product._hayCompact.includes(compactToken));
    });
  }

  function fieldMatchesSearchToken(normalizedField, compactField, token) {
    return [token].concat(searchTokenAliases(token)).some((item) => {
      const normalized = normalizeText(item);
      const compactToken = compactSearchText(item);
      return (normalized && normalizedField.includes(normalized)) || (compactToken.length >= 4 && compactField.includes(compactToken));
    });
  }

  function fieldStartsWithSearchToken(normalizedField, compactField, token) {
    return [token].concat(searchTokenAliases(token)).some((item) => {
      const normalized = normalizeText(item);
      const compactToken = compactSearchText(item);
      return (normalized && normalizedField.startsWith(normalized)) || (compactToken.length >= 4 && compactField.startsWith(compactToken));
    });
  }

  function scoreStructuredSearchFields(product, tokens, phrase) {
    const origin = product._onorm;
    const originCompact = product._ocompact;
    const type = product._typenorm;
    const typeCompact = product._typecompact;
    const platform = product._platformnorm;
    const platformCompact = product._platformcompact;
    const title = product._tnorm;
    const titleCompact = product._tcompact;
    const compactPhrase = compactSearchText(phrase);
    let score = 0;

    if (origin) {
      if (origin === phrase || (compactPhrase.length >= 4 && originCompact === compactPhrase)) score += 130;
      else if (origin.includes(phrase) || (compactPhrase.length >= 4 && originCompact.includes(compactPhrase))) score += 95;
      else if (tokens.some((token) => fieldStartsWithSearchToken(origin, originCompact, token))) score += 45;
      else if (tokens.some((token) => fieldMatchesSearchToken(origin, originCompact, token))) score += 28;
    }

    for (const token of tokens) {
      if (fieldMatchesSearchToken(type, typeCompact, token)) score += 38;
      if (fieldMatchesSearchToken(platform, platformCompact, token)) score += 24;
    }

    const matchedInOrigin = tokens.filter((token) => fieldMatchesSearchToken(origin, originCompact, token)).length;
    const matchedInType = tokens.filter((token) => fieldMatchesSearchToken(type, typeCompact, token) || fieldMatchesSearchToken(platform, platformCompact, token)).length;
    const matchedInTitle = tokens.filter((token) => fieldMatchesSearchToken(title, titleCompact, token)).length;
    if (matchedInOrigin && matchedInType) score += 70;
    else if (matchedInOrigin && matchedInTitle >= Math.max(1, tokens.length - matchedInOrigin)) score += 35;
    return score;
  }

  function scoreProductCached(product, tokens, phrase) {
    if (!tokens.length) return 1;
    ensureSearchCache(product);
    for (let i = 0; i < tokens.length; i += 1) {
      if (!matchesSearchToken(product, tokens[i])) return 0;
    }
    const title = product._tnorm;
    const titleCompact = product._tcompact;
    const vendor = product._vnorm;
    const vendorCompact = product._vcompact;
    const ean = product._eanNorm;
    const compactPhrase = compactSearchText(phrase);
    let score = 10;
    if (ean && tokens.some((token) => token.length >= 8 && ean.includes(token))) score += 120;
    score += scoreStructuredSearchFields(product, tokens, phrase);
    if (title.includes(phrase)) score += 80;
    else if (compactPhrase.length >= 4 && titleCompact.includes(compactPhrase)) score += 70;
    if (title.startsWith(tokens[0])) score += 30;
    if (tokens.some((token) => fieldMatchesSearchToken(vendor, vendorCompact, token))) score += 12;
    for (const token of tokens) {
      if (fieldMatchesSearchToken(title, titleCompact, token)) score += 8;
    }
    if (product.available) score += 3;
    if (product.preorder) score += 2;
    score += Math.min(Number(product.sold || 0), 200) / 50;
    return score;
  }

  function scoreProduct(product, query) {
    const tokens = searchTokens(query);
    return scoreProductCached(product, tokens, tokens.join(" "));
  }

  function scoreProductTitleOnly(product, query) {
    const tokens = searchTokens(query);
    if (!tokens.length) return 1;
    const title = normalizeText(product.title);
    const titleCompact = compactSearchText(product.title);
    if (!tokens.every((token) => fieldMatchesSearchToken(title, titleCompact, token))) return 0;

    let score = 10;
    const phrase = tokens.join(" ");
    const compactPhrase = compactSearchText(phrase);
    if (title.includes(phrase)) score += 80;
    else if (compactPhrase.length >= 4 && titleCompact.includes(compactPhrase)) score += 70;
    if (title.startsWith(tokens[0])) score += 30;
    for (const token of tokens) {
      if (fieldMatchesSearchToken(title, titleCompact, token)) score += 8;
    }
    if (product.available) score += 3;
    if (product.preorder) score += 2;
    score += Math.min(Number(product.sold || 0), 200) / 50;
    return score;
  }

  function valuesOf(product, prop, blocklist, filterKey) {
    const filter = FILTERS.find((item) => item.key === filterKey);
    const value = filter && filter.customKey ? (product.custom || {})[filter.customKey] : product[prop];
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((item) => canonicalFilterValue(blocklist, filterKey, item))
      .filter((item) => item && !isBlockedFilterValue(blocklist, filterKey, item));
  }

  function productStocks(product) {
    const stocks = [];
    if (product.available) stocks.push("available");
    if (product.preorder) stocks.push("preorder");
    if (!product.available && !product.preorder) stocks.push("soldout");
    return stocks;
  }

  function matchesPrice(product, selected) {
    if (!selected.priceMin && !selected.priceMax) return true;
    const price = Number(product.price || 0);
    const min = Number(String(selected.priceMin || "").replace(",", "."));
    const max = Number(String(selected.priceMax || "").replace(",", "."));
    if (price <= 0) return false;
    if (selected.priceMin && !Number.isNaN(min) && price < min) return false;
    if (selected.priceMax && !Number.isNaN(max) && price > max) return false;
    return true;
  }

  function isFutureRelease(product) {
    if (!product.preorder || !product.release_date || !product.release_sort) return false;
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    return String(product.release_sort) >= currentMonth;
  }

  function productMatches(product, selected, exceptKey, blocklist) {
    return FILTERS.every((filter) => {
      if (filter.key === exceptKey) return true;
      if (filter.key === "price") {
        return matchesPrice(product, selected);
      }
      const chosen = selected[filter.key];
      if (!chosen || chosen.size === 0) return true;
      if (filter.key === "stock") {
        return productStocks(product).some((value) => chosen.has(value));
      }
      if (filter.key === "sale") {
        return product.sale && chosen.has("sale");
      }
      return valuesOf(product, filter.prop, blocklist, filter.key).some((value) => chosen.has(value));
    });
  }

  function scopedProducts(products, handle, indexCollectionHandle) {
    if (!handle || handle === "all") return products;
    if (indexCollectionHandle === handle) return products;
    if (indexCollectionHandle || !products.some((product) => Array.isArray(product.collections) && product.collections.length)) return null;
    return products.filter((product) => Array.isArray(product.collections) && product.collections.includes(handle));
  }

  function firstNumber(value) {
    const match = String(value || "").replace(",", ".").match(/(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
  }

  function scaleNumber(value) {
    const text = String(value || "");
    const fraction = text.match(/(\d+)\s*\/\s*(\d+)/);
    if (fraction) return Number(fraction[2]) || Number.POSITIVE_INFINITY;
    return firstNumber(text);
  }

  function compareFilterOptions(filter, a, b) {
    if (a.count === 0 && b.count !== 0) return 1;
    if (b.count === 0 && a.count !== 0) return -1;
    if (filter.key === "size") {
      const diff = firstNumber(a.label) - firstNumber(b.label);
      if (diff) return diff;
      return String(a.label).localeCompare(String(b.label), "de", { numeric: true, sensitivity: "base" });
    }
    if (filter.key === "scale") {
      const diff = scaleNumber(a.label) - scaleNumber(b.label);
      if (diff) return diff;
      return String(a.label).localeCompare(String(b.label), "de", { numeric: true, sensitivity: "base" });
    }
    return b.count - a.count || String(a.label).localeCompare(String(b.label), "de", { numeric: true, sensitivity: "base" });
  }

  function countOptions(products, filter, sourceOptions, blocklist) {
    if (filter.range) return [];
    if (filter.fixed) {
      return filter.fixed.map(([value, label]) => {
        let count = 0;
        for (const product of products) {
          if (filter.key === "stock" && productStocks(product).includes(value)) count += 1;
          if (filter.key === "sale" && product.sale) count += 1;
        }
        return { value, label, count };
      }).filter((option) => option.count > 0 || sourceOptions);
    }

    const counts = new Map();
    for (const product of products) {
      for (const value of valuesOf(product, filter.prop, blocklist, filter.key)) {
        counts.set(value, (counts.get(value) || 0) + 1);
      }
    }
    const options = sourceOptions || Array.from(counts, ([value]) => ({ value, label: value }));
    return options
      .map((option) => {
        const value = publicFilterValue(option.value);
        if (!value || isBlockedFilterValue(blocklist, filter.key, value)) return null;
        return { ...option, value, label: publicFilterValue(option.label) || value, count: counts.get(value) || 0 };
      })
      .filter((option) => option && (option.count > 0 || sourceOptions))
      .sort((a, b) => compareFilterOptions(filter, a, b));
  }

  function readInitialState() {
    const params = new URLSearchParams(window.location.search);
    const selected = {};
    for (const filter of FILTERS) {
      selected[filter.key] = new Set();
      const value = params.get(`gf_${filter.key}`);
      if (value) {
        value.split("|").filter(Boolean).forEach((item) => selected[filter.key].add(decodeURIComponent(item)));
      }
    }

    const oldStock = params.get("rb_stock_status");
    if (oldStock && selected.stock.size === 0) {
      if (oldStock.includes("VERFUEGBAR")) selected.stock.add("available");
      if (oldStock.includes("VORBESTELLER")) selected.stock.add("preorder");
      if (oldStock.includes("AUSVERKAUFT")) selected.stock.add("soldout");
    }

    for (const [key, value] of params.entries()) {
      if (key.startsWith("rb_filter_ptag") && value && selected.type.size === 0) {
        value.split("|").forEach((item) => selected.type.add(item));
      }
    }
    selected.priceMin = params.get("gf_price_min") || "";
    selected.priceMax = params.get("gf_price_max") || "";
    return selected;
  }

  function writeState(selected) {
    const params = new URLSearchParams(window.location.search);
    for (const filter of FILTERS) {
      const values = Array.from(selected[filter.key] || []);
      if (values.length) {
        params.set(`gf_${filter.key}`, values.map(encodeURIComponent).join("|"));
      } else {
        params.delete(`gf_${filter.key}`);
      }
    }
    for (const key of Array.from(params.keys())) {
      if (key === "rb_stock_status" || key.startsWith("rb_filter_ptag")) params.delete(key);
    }
    if (selected.priceMin) params.set("gf_price_min", selected.priceMin);
    else params.delete("gf_price_min");
    if (selected.priceMax) params.set("gf_price_max", selected.priceMax);
    else params.delete("gf_price_max");
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
    window.history.replaceState({}, "", next);
  }

  function validSort(value, fallback, sorts = SORTS) {
    return sorts.some(([sortKey]) => sortKey === value) ? value : fallback;
  }

  function readSortState(defaultSort) {
    const params = new URLSearchParams(window.location.search);
    const value = params.get("gf_sort");
    return validSort(value, defaultSort);
  }

  function readQuickSearchState() {
    return new URLSearchParams(window.location.search).get("gf_q") || "";
  }

  function writeQuickSearchState(query) {
    const params = new URLSearchParams(window.location.search);
    const clean = String(query || "").trim();
    if (clean) params.set("gf_q", clean);
    else params.delete("gf_q");
    window.history.replaceState({}, "", `${window.location.pathname}${params.toString() ? `?${params}` : ""}`);
  }

  function writeSortState(sortValue, defaultSort) {
    const params = new URLSearchParams(window.location.search);
    if (sortValue && sortValue !== defaultSort) {
      params.set("gf_sort", sortValue);
    } else {
      params.delete("gf_sort");
    }
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
    window.history.replaceState({}, "", next);
  }

  function timeValue(value) {
    const parsed = Date.parse(value || "");
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  function productFamilyKey(product) {
    const id = String(product.id || "").trim();
    if (id) return `id:${id}`;
    return product;
  }

  function stockRank(product) {
    if (product.available) return 3;
    if (product.preorder) return 2;
    return 1;
  }

  function chooseDisplayProduct(candidate, current) {
    if (!current) return candidate;
    const rankDiff = stockRank(candidate) - stockRank(current);
    if (rankDiff !== 0) return rankDiff > 0 ? candidate : current;
    const candidatePrice = Number(candidate.price || 0);
    const currentPrice = Number(current.price || 0);
    if (candidatePrice > 0 && currentPrice > 0 && candidatePrice !== currentPrice) {
      return candidatePrice < currentPrice ? candidate : current;
    }
    if (candidatePrice > 0 && currentPrice <= 0) return candidate;
    if (candidatePrice <= 0 && currentPrice > 0) return current;
    const candidateScore = Number(candidate._genkiScore || 0);
    const currentScore = Number(current._genkiScore || 0);
    if (candidateScore !== currentScore) return candidateScore > currentScore ? candidate : current;
    const candidateTime = timeValue(candidate.updated_at || candidate.created_at);
    const currentTime = timeValue(current.updated_at || current.created_at);
    if (candidateTime !== currentTime) return candidateTime > currentTime ? candidate : current;
    return Number(candidate.id || 0) > Number(current.id || 0) ? candidate : current;
  }

  function collapseProductFamilies(products) {
    const byFamily = new Map();
    const familyCounts = new Map();
    for (const product of products) {
      const key = productFamilyKey(product);
      familyCounts.set(key, (familyCounts.get(key) || 0) + 1);
      const current = byFamily.get(key);
      const chosen = chooseDisplayProduct(product, current);
      if (current && chosen) {
        chosen._genkiScore = Math.max(Number(current._genkiScore || 0), Number(product._genkiScore || 0));
      }
      byFamily.set(key, chosen);
    }
    return Array.from(byFamily.entries()).map(([key, product]) => {
      product._genkiFamilyCount = familyCounts.get(key) || 1;
      return product;
    });
  }

  function sortProducts(products, sortValue) {
    const sorted = products.slice();
    sorted.sort((a, b) => {
      if (sortValue === "best_selling") {
        if (Number(b.sold || 0) !== Number(a.sold || 0)) return Number(b.sold || 0) - Number(a.sold || 0);
        return timeValue(b.updated_at) - timeValue(a.updated_at) || Number(b.id || 0) - Number(a.id || 0);
      }
      if (sortValue === "relevance") {
        if (Number(b._genkiScore || 0) !== Number(a._genkiScore || 0)) {
          return Number(b._genkiScore || 0) - Number(a._genkiScore || 0);
        }
        return timeValue(b.updated_at) - timeValue(a.updated_at) || Number(b.id || 0) - Number(a.id || 0);
      }
      if (sortValue === "created_desc") return timeValue(b.created_at) - timeValue(a.created_at) || Number(b.id || 0) - Number(a.id || 0);
      if (sortValue === "price_asc") return Number(a.price || 0) - Number(b.price || 0);
      if (sortValue === "price_desc") return Number(b.price || 0) - Number(a.price || 0);
      if (sortValue === "title_asc") return String(a.title).localeCompare(String(b.title), "de");
      if (sortValue === "title_desc") return String(b.title).localeCompare(String(a.title), "de");
      return timeValue(b.updated_at) - timeValue(a.updated_at) || Number(b.id || 0) - Number(a.id || 0);
    });
    return sorted;
  }

  function selectedLabel(filter, selected) {
    if (filter.range) {
      if (selected.priceMin && selected.priceMax) return `${filter.label} (${selected.priceMin}-${selected.priceMax})`;
      if (selected.priceMin) return `${filter.label} (ab ${selected.priceMin})`;
      if (selected.priceMax) return `${filter.label} (bis ${selected.priceMax})`;
      return filter.label;
    }
    const amount = selected[filter.key].size;
    return amount ? `${filter.label} (${amount})` : filter.label;
  }

  function optionSearch(filter, options) {
    if (options.length < 8) return "";
    return `
      <div class="genki-filter__search-wrap">
        <input
          class="genki-filter__search"
          type="search"
          placeholder="Search options"
          autocomplete="off"
          aria-label="${escapeHtml(filter.label)} durchsuchen"
        >
      </div>
    `;
  }

  function optionMarkup(option, selectedSet) {
    const disabled = option.count === 0 && !selectedSet.has(option.value);
    return `
      <label class="genki-filter__option${disabled ? " is-disabled" : ""}" data-search="${escapeHtml(option.label.toLowerCase())}">
        <input type="checkbox" value="${escapeHtml(option.value)}"${selectedSet.has(option.value) ? " checked" : ""}${disabled ? " disabled" : ""}>
        <span>${escapeHtml(option.label)} (${option.count})</span>
      </label>
    `;
  }

  function visibleOptions(options, selectedSet, term, filterKey = "", limit = OPTION_LIMIT) {
    const needle = term.trim().toLowerCase();
    const matched = needle
      ? options.filter((option) => option.label.toLowerCase().includes(needle))
      : options;
    const selectedOptions = options.filter((option) => selectedSet.has(option.value));
    const merged = [];
    const seen = new Set();
    const targetLimit = filterKey === "type" && !needle
      ? Math.max(Number(limit) || OPTION_LIMIT, selectedOptions.length)
      : OPTION_LIMIT;
    for (const option of selectedOptions.concat(matched)) {
      if (seen.has(option.value)) continue;
      seen.add(option.value);
      merged.push(option);
      if (filterKey === "type" && needle) continue;
      if (filterKey === "type" && merged.length >= targetLimit) break;
      if (filterKey !== "type" && !needle && merged.length >= OPTION_LIMIT) break;
      if (filterKey !== "type" && needle && merged.length >= OPTION_LIMIT * 2) break;
    }
    return merged;
  }

  function renderOptionList(panel, options, selectedSet, term) {
    const list = panel.querySelector(".genki-filter__options");
    const filterKey = panel.closest(".genki-filter__select")?.dataset.filter || "";
    const optionLimit = Number(panel.dataset.optionLimit || OPTION_LIMIT);
    const shown = visibleOptions(options, selectedSet, term, filterKey, optionLimit);
    const canShowMore = filterKey === "type" && !term.trim() && shown.length < options.length;
    list.innerHTML = `
      ${shown.map((option) => optionMarkup(option, selectedSet)).join("")}
      <div class="genki-filter__no-options"${shown.length ? " hidden" : ""}>Keine Optionen gefunden.</div>
      ${canShowMore ? `<button class="genki-filter__option-more" type="button">Mehr zeigen (${shown.length.toLocaleString("de-DE")}/${options.length.toLocaleString("de-DE")})</button>` : ""}
      ${!term && options.length > shown.length ? `<div class="genki-filter__hint">Tippen zum Suchen in ${options.length.toLocaleString("de-DE")} Optionen</div>` : ""}
    `;
  }

  function priceRangeMarkup(selected) {
    return `
      <div class="genki-filter__price-range">
        <label>
          <span>Von</span>
          <input class="genki-filter__price-input" type="number" min="0" inputmode="decimal" data-price-bound="min" value="${escapeHtml(selected.priceMin || "")}" placeholder="0">
        </label>
        <label>
          <span>Bis</span>
          <input class="genki-filter__price-input" type="number" min="0" inputmode="decimal" data-price-bound="max" value="${escapeHtml(selected.priceMax || "")}" placeholder="999">
        </label>
      </div>
    `;
  }

  function renderShell(root, optionsByFilter, selected, sortOptions = SORTS) {
    const title = root.dataset.title || "Genkidama";
    root.innerHTML = `
      <div class="genki-filter__inner">
        <h2 class="genki-filter__title">${escapeHtml(title)}</h2>
        <div class="genki-filter__stock-tabs" aria-label="Lagerstatus"></div>
        <button class="genki-filter__mobile-open" type="button" aria-expanded="false" aria-controls="GenkiFilterPanel">
          <span>FILTER</span>
        </button>
        <div class="genki-filter__bar"></div>
        <div class="genki-filter__quick-search" role="search">
          <label class="genki-filter__quick-label">
            <span class="genki-filter__quick-caption">Suche in den Ergebnissen</span>
            <input class="genki-filter__quick-input" type="search" placeholder="z. B. 007, Naruto, PS5..." autocomplete="off">
          </label>
        </div>
        <div class="genki-filter__status">
          <span class="genki-filter__count">Produkte werden geladen...</span>
          <label class="genki-filter__sort-label">
            <span>Sortieren</span>
            <select class="genki-filter__sort">
              ${sortOptions.map(([value, label]) => `<option value="${value}">${escapeHtml(label)}</option>`).join("")}
            </select>
          </label>
          <button class="genki-filter__reset" type="button">Alle Filter zurücksetzen</button>
        </div>
        <div class="genki-filter__loading" role="status" aria-live="polite" hidden>
          <span class="genki-filter__loading-spinner" aria-hidden="true"></span>
          <span class="genki-filter__loading-text"></span>
        </div>
        <ul class="genki-filter__grid"></ul>
        <div class="genki-filter__empty" hidden>Keine Produkte gefunden.</div>
        <div class="genki-filter__more" hidden>
          <button class="button genki-filter__more-button" type="button">Mehr laden</button>
        </div>
      </div>
    `;

    const bar = root.querySelector(".genki-filter__bar");
    bar.id = "GenkiFilterPanel";
    bar.innerHTML = DROPDOWN_FILTERS.map((filter) => {
      const options = optionsByFilter[filter.key] || [];
      return `
        <details class="genki-filter__select" data-filter="${escapeHtml(filter.key)}">
          <summary class="genki-filter__summary">${escapeHtml(selectedLabel(filter, selected))}</summary>
          <div class="genki-filter__panel">
            <div class="genki-filter__meta">${selected[filter.key].size} selected</div>
            ${filter.range ? priceRangeMarkup(selected) : optionSearch(filter, options)}
            ${filter.range ? "" : '<div class="genki-filter__options"></div>'}
          </div>
        </details>
      `;
    }).join("") + `
      <div class="genki-filter__mobile-actions">
        <button class="genki-filter__mobile-close" type="button">Filter schliessen</button>
      </div>
    `;

    for (const filter of DROPDOWN_FILTERS) {
      if (filter.range) continue;
      const panel = root.querySelector(`[data-filter="${filter.key}"] .genki-filter__panel`);
      if (panel) renderOptionList(panel, optionsByFilter[filter.key] || [], selected[filter.key], "");
    }
  }

  function renderStockTabs(root, optionsByFilter, selected, progressive) {
    const tabs = root.querySelector(".genki-filter__stock-tabs");
    if (!tabs) return;
    const stockFilter = FILTERS.find((filter) => filter.key === "stock");
    let options = optionsByFilter.stock && optionsByFilter.stock.length ? optionsByFilter.stock : countOptions([], stockFilter);
    if (progressive) {
      const labels = { available: "Verfügbar", preorder: "Vorbesteller", soldout: "Ausverkauft" };
      options = ["available", "preorder", "soldout"].map((value) => {
        const current = options.find((option) => option.value === value);
        const pending = value === "soldout" ? !progressive.soldoutLoaded : !progressive.readyLoaded;
        return { value, label: labels[value], count: pending ? progressive.stockCounts[value] : Number(current?.count || 0) };
      });
    }
    tabs.innerHTML = options
      .filter((option) => ["available", "preorder", "soldout"].includes(option.value))
      .map((option) => {
        const active = selected.stock.has(option.value);
        const state = option.value === "available" ? "available" : option.value === "preorder" ? "preorder" : "soldout";
        return `
          <button class="genki-filter__stock-tab genki-filter__stock-tab--${state}${active ? " is-active" : ""}" type="button" data-stock="${escapeHtml(option.value)}">
            <span>${escapeHtml(option.label)}</span>
            <strong>${Number(option.count || 0).toLocaleString("de-DE")}</strong>
          </button>
        `;
      })
      .join("");
  }

  function badge(product) {
    if (product.preorder) return "Vorbesteller";
    if (product.available) return "Verfügbar";
    return "Ausverkauft";
  }

  function badgeState(product) {
    if (product.preorder) return "preorder";
    if (product.available) return "available";
    return "soldout";
  }

  function isFsk18(product) {
    const haystack = [
      product.title,
      product.origin,
      ...(Array.isArray(product.type) ? product.type : [product.type]),
      ...(Array.isArray(product.platform) ? product.platform : [product.platform]),
    ].join(" ").toLowerCase();
    return haystack.includes("hentai") || haystack.includes("18+");
  }

  function fskVerified() {
    const verified = localStorage.getItem("fsk18_verified");
    const verifiedTime = Number(localStorage.getItem("fsk18_time") || 0);
    const oneDay = 24 * 60 * 60 * 1000;
    if (verified === "true" && verifiedTime && Date.now() - verifiedTime < oneDay) return true;
    localStorage.removeItem("fsk18_verified");
    localStorage.removeItem("fsk18_time");
    return false;
  }

  function revealFsk18() {
    document.body.classList.add("genki-fsk18-verified");
    document.querySelectorAll(".genki-filter__card--fsk18").forEach((card) => {
      card.classList.add("is-fsk18-open");
    });
  }

  function imageUrl(src, width) {
    if (!src) return "";
    const separator = src.includes("?") ? "&" : "?";
    return `${src}${separator}width=${width}`;
  }

  function promoBadgesMarkup(product, isSale) {
    const createdAt = timeValue(product.created_at);
    const age = Date.now() - createdAt;
    const isNew = createdAt > 0 && age >= 0 && age < NEW_BADGE_LIFETIME_MS;
    const badges = [];
    if (isNew) {
      badges.push('<span class="genki-filter__promo genki-filter__promo--new">NEW</span>');
    }
    if (isSale) {
      badges.push('<span class="genki-filter__promo genki-filter__promo--sale">SALE</span>');
    }
    return badges.length ? `<span class="genki-filter__promos">${badges.join("")}</span>` : "";
  }

  async function genkiIndexFetch(url) {
    // ERSATZWEG (05.08.): Die Kundendomain liefert fuer grosse JSON-Assets
    // zeitweise 502/504 (Proxy/CDN-Schicht). Erst normal versuchen, bei
    // Fehler direkt vom Shopify-CDN laden. FAIL-OPEN bleibt erhalten.
    const freshUrl = (rawUrl) => {
      const u = new URL(String(rawUrl || ""), window.location.href);
      u.searchParams.set("_genki_index_t", String(Date.now()));
      return u.toString();
    };
    const indexPath = new URL(String(url || ""), window.location.href).pathname;
    const firstSegment = /-first\.json$/.test(indexPath);
    const segmented = /-(first|ready|soldout)\.json$/.test(indexPath);
    try {
      const r = await fetch(segmented && !firstSegment ? url : freshUrl(url), { credentials: "same-origin", cache: segmented && !firstSegment ? "default" : "no-store" });
      if (r.ok) return r;
    } catch (e) { /* weiter zum Ersatzweg */ }
    const m = String(url || "").match(/\/cdn\/shop\/(t\/\d+\/assets\/[^"']+)$/);
    if (m) {
      const direkt = "https://figuna.myshopify.com/cdn/shop/" + m[1];
      const r2 = await fetch(freshUrl(direkt), { mode: "cors", cache: "no-store" });
      if (r2.ok) return r2;
    }
    throw new Error("Genkidama-Index nicht ladbar");
  }

  function renderCard(product, index) {
    const numericPrice = Number(product.price || 0);
    const numericCompareAt = Number(product.compare_at || 0);
    const hasPrice = numericPrice > 0;
    const pricePrefix = product.price_varies || Number(product._genkiFamilyCount || 1) > 1 ? "ab " : "";
    const isSale = hasPrice && numericCompareAt > numericPrice;
    const priceHtml = !hasPrice ? "" : `<div class="genki-filter__price genki-filter__price-badge${isSale ? ' genki-filter__price--sale' : ''}">
      <span class="genki-filter__sale-price">${pricePrefix}${currency.format(numericPrice)}</span>
      ${isSale ? `<span class="genki-filter__compare">Vorher <s>${currency.format(numericCompareAt)}</s></span>` : ''}
    </div>`;
    const ratingHtml = `<div class="genki-filter__rating genkidama-card-rating" data-genkidama-card-rating data-product-id="${escapeHtml(product.id)}" data-product-handle="${escapeHtml(product.handle)}" aria-label="Bewertungen werden geladen"><span aria-hidden="true">☆☆☆☆☆</span></div>`;
    const showRelease = Boolean(product.preorder && product.release_date);
    const releaseHtml = showRelease
      ? `<div class="genki-filter__release${product.release_changed ? " genki-filter__release--changed" : ""}">
          <span>${product.release_changed ? "Neuer Termin" : "Release"}</span>
          <strong>${escapeHtml(product.release_date)}</strong>
        </div>`
      : "";
    const fsk18 = isFsk18(product);
    const image = product.image
      ? `<img class="genki-filter__image${fsk18 ? " genki-filter__image--fsk18" : ""}" src="${escapeHtml(imageUrl(product.image, 360))}" srcset="${escapeHtml(imageUrl(product.image, 240))} 240w, ${escapeHtml(imageUrl(product.image, 360))} 360w, ${escapeHtml(imageUrl(product.image, 520))} 520w" sizes="(max-width: 749px) 46vw, (max-width: 989px) 30vw, 22vw" alt="${escapeHtml(product.title)}" loading="${index < 6 ? "eager" : "lazy"}" decoding="async">`
      : "";
    const state = badgeState(product);
    return `
      <li class="genki-filter__card genki-filter__card--${state}${fsk18 ? " genki-filter__card--fsk18" : ""}">
        <a class="genki-filter__image-wrap" href="${escapeHtml(product.url)}"${fsk18 ? ' data-fsk18="true"' : ""}>
          ${image}
          ${fsk18 ? `
            <span class="genki-filter__fsk-lock" aria-hidden="true">
              <span class="genki-filter__fsk-title">⚠ FSK 18+</span>
              <span class="genki-filter__fsk-sub">Klicken zum Öffnen</span>
            </span>
          ` : ""}
          <span class="genki-filter__ribbon genki-filter__ribbon--${state}">${badge(product)}</span>
          ${promoBadgesMarkup(product, isSale)}
          ${priceHtml}
        </a>
        <a class="genki-filter__name" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
        ${product.vendor && product.vendor !== "-" ? `<div class="genki-filter__vendor">${escapeHtml(product.vendor)}</div>` : ""}
        ${releaseHtml}
        ${ratingHtml}
      </li>
    `;
  }

  function init(root, products, blocklist, indexCollectionHandle, progressive = null) {
    const isSearchPage = root.dataset.searchPage === "true";
    const query = root.dataset.searchQuery || new URLSearchParams(window.location.search).get("q") || "";
    const scoped = scopedProducts(products, root.dataset.collectionHandle || "", indexCollectionHandle);
    if (scoped === null) throw new Error("Index has no verified collection scope");
    let baseProducts = scoped;
    const selected = readInitialState();
    const sortOptions = isSearchPage ? [["relevance", "Relevanz"], ...SORTS] : SORTS;
    const defaultSort = validSort(root.dataset.defaultSort, DEFAULT_SORT, sortOptions);
    let sortValue = validSort(readSortState(defaultSort), defaultSort, sortOptions);
    let filterQuery = isSearchPage && query ? String(query).trim() : readQuickSearchState();
    const allOptionsByFilter = {};
    const optionsByFilter = {};
    for (const filter of FILTERS) {
      allOptionsByFilter[filter.key] = countOptions(baseProducts, filter, null, blocklist);
      optionsByFilter[filter.key] = allOptionsByFilter[filter.key];
    }

    let visibleCount = PAGE_SIZE;
    renderShell(root, optionsByFilter, selected, sortOptions);
    root.classList.add("is-ready");
    document.body.classList.add("genki-filter-active");
    if (fskVerified()) revealFsk18();

    const grid = root.querySelector(".genki-filter__grid");
    const count = root.querySelector(".genki-filter__count");
    const empty = root.querySelector(".genki-filter__empty");
    const loading = root.querySelector(".genki-filter__loading");
    const more = root.querySelector(".genki-filter__more");
    const moreButton = root.querySelector(".genki-filter__more-button");
    const sortSelect = root.querySelector(".genki-filter__sort");
    const quickInput = root.querySelector(".genki-filter__quick-input");
    const quickClear = root.querySelector(".genki-filter__quick-clear");
    sortSelect.value = sortValue;
    if (quickInput) quickInput.value = filterQuery;

    function viewStateKey() {
      return `genki-filter-view:${window.location.pathname}:${window.location.search}:${filterQuery}`;
    }

    function saveViewState() {
      try {
        sessionStorage.setItem(viewStateKey(), JSON.stringify({
          visibleCount,
          scrollY: window.scrollY || window.pageYOffset || 0,
        }));
      } catch (error) {
        // sessionStorage can be unavailable in strict privacy modes.
      }
    }

    function clearViewState() {
      try {
        sessionStorage.removeItem(viewStateKey());
      } catch (error) {
        // Ignore storage cleanup failures.
      }
    }

    function resetVisibleCount() {
      visibleCount = PAGE_SIZE;
      clearViewState();
    }

    let restoreScrollY = null;
    try {
      const saved = JSON.parse(sessionStorage.getItem(viewStateKey()) || "null");
      if (saved && Number(saved.visibleCount) > PAGE_SIZE) {
        visibleCount = Math.max(PAGE_SIZE, Math.min(Number(saved.visibleCount), 3000));
        restoreScrollY = Number(saved.scrollY || 0);
      }
    } catch (error) {
      restoreScrollY = null;
    }

    function syncLabels() {
      for (const filter of FILTERS) {
        const details = root.querySelector(`[data-filter="${filter.key}"]`);
        if (!details) continue;
        const summary = details.querySelector(".genki-filter__summary");
        const meta = details.querySelector(".genki-filter__meta");
        summary.textContent = selectedLabel(filter, selected);
        meta.textContent = `${selected[filter.key].size} selected`;
      }
      renderStockTabs(root, optionsByFilter, selected, progressive);
    }

    function refreshOptions(quickProductsPrecomputed) {
      // PERFORMANCE (26.07.): Die text-gefilterte Liste kommt fertig aus
      // render() - vorher wurde hier fuer alle Produkte NOCHMAL der komplette
      // Suchtext berechnet (doppelte Arbeit pro Tastendruck).
      const quickProducts = quickProductsPrecomputed || (filterQuery
        ? baseProducts.filter((product) => scoreProduct(product, filterQuery) > 0)
        : baseProducts);
      for (const filter of FILTERS) {
        if (filter.range) continue;
        const contextualProducts = quickProducts.filter((product) => productMatches(product, selected, filter.key, blocklist));
        optionsByFilter[filter.key] = countOptions(contextualProducts, filter, allOptionsByFilter[filter.key], blocklist);
        const panel = root.querySelector(`[data-filter="${filter.key}"] .genki-filter__panel`);
        if (!panel) continue;
        const search = panel.querySelector(".genki-filter__search");
        renderOptionList(panel, optionsByFilter[filter.key], selected[filter.key], search ? search.value : "");
      }
    }

    function render(renderOpts) {
      const opts = renderOpts || {};
      // PERFORMANCE (26.07.): Ein Durchlauf statt zwei; Scores werden direkt
      // am Produkt gespeichert (kein Kopieren von 20.000+ Objekten mehr).
      const tokens = searchTokens(filterQuery);
      const phrase = tokens.join(" ");
      let quickProducts;
      let textFiltered;
      if (tokens.length) {
        quickProducts = [];
        textFiltered = [];
        for (const product of baseProducts) {
          const score = scoreProductCached(product, tokens, phrase);
          if (score <= 0) continue;
          product._genkiScore = score;
          quickProducts.push(product);
          if (productMatches(product, selected, null, blocklist)) textFiltered.push(product);
        }
      } else {
        quickProducts = baseProducts;
        textFiltered = baseProducts.filter((product) => productMatches(product, selected, null, blocklist));
      }
      const quickFamilies = collapseProductFamilies(quickProducts);
      const filtered = sortProducts(collapseProductFamilies(textFiltered), sortValue);
      const shown = filtered.slice(0, visibleCount);
      grid.innerHTML = shown.map((product, index) => renderCard(product, index)).join("");
      if (window.GenkidamaCardRatings && typeof window.GenkidamaCardRatings.load === "function") {
        window.GenkidamaCardRatings.load(grid);
      }
      const pending = progressive && (progressive.readyLoading || (selected.stock.has("soldout") && progressive.soldoutLoading));
      count.textContent = `${filtered.length.toLocaleString("de-DE")} Ergebnisse${pending ? " · weitere werden eingelesen" : ""}`;
      empty.hidden = filtered.length !== 0 || !!pending;
      more.hidden = shown.length >= filtered.length;
      if (quickClear) quickClear.hidden = !filterQuery;
      // "Mehr laden" aendert nur die Anzahl - Filter-Zaehler bleiben gleich.
      if (!opts.skipOptions) refreshOptions(quickFamilies);
      syncLabels();
      if (fskVerified()) revealFsk18();
      if (restoreScrollY !== null) {
        const y = restoreScrollY;
        restoreScrollY = null;
        window.requestAnimationFrame(() => {
          window.setTimeout(() => window.scrollTo(0, y), 40);
        });
      }
    }

    root.addEventListener("toggle", (event) => {
      if (!event.target.matches(".genki-filter__select") || !event.target.open) return;
      root.querySelectorAll(".genki-filter__select[open]").forEach((details) => {
        if (details !== event.target) details.removeAttribute("open");
      });
    }, true);

    root.addEventListener("click", (event) => {
      const summary = event.target.closest(".genki-filter__summary");
      if (summary && root.classList.contains("is-mobile-filter-open")) {
        const details = summary.closest(".genki-filter__select");
        if (details) {
          event.preventDefault();
          event.stopPropagation();
          const shouldOpen = !details.open;
          root.querySelectorAll(".genki-filter__select[open]").forEach((openDetails) => {
            if (openDetails !== details) openDetails.removeAttribute("open");
          });
          details.open = shouldOpen;
          return;
        }
      }

      const showMoreOptions = event.target.closest(".genki-filter__option-more");
      if (showMoreOptions) {
        const panel = showMoreOptions.closest(".genki-filter__panel");
        const details = showMoreOptions.closest(".genki-filter__select");
        const key = details?.dataset.filter || "";
        const search = panel?.querySelector(".genki-filter__search");
        if (!panel || key !== "type") return;
        const currentLimit = Number(panel.dataset.optionLimit || OPTION_LIMIT) || OPTION_LIMIT;
        panel.dataset.optionLimit = String(currentLimit + TYPE_OPTION_STEP);
        renderOptionList(panel, optionsByFilter[key] || [], selected[key], search?.value || "");
        return;
      }

      const button = event.target.closest(".genki-filter__stock-tab");
      if (!button) return;
      const value = button.dataset.stock;
      if (!value) return;
      if (selected.stock.has(value)) selected.stock.delete(value);
      else selected.stock.add(value);
      if (value === "soldout" && selected.stock.has("soldout") && progressive?.loadSoldout) {
        progressive.loadSoldout();
      }
      window.GenkiAnalytics?.filterUsed?.("stock", value, selected.stock.has(value));
      resetVisibleCount();
      writeState(selected);
      render();
    });

    root.addEventListener("change", (event) => {
      const input = event.target;
      if (input.matches(".genki-filter__price-input")) {
        const bound = input.dataset.priceBound;
        if (bound === "min") selected.priceMin = input.value;
        if (bound === "max") selected.priceMax = input.value;
        window.GenkiAnalytics?.filterPrice?.(selected.priceMin, selected.priceMax);
        resetVisibleCount();
        writeState(selected);
        render();
        return;
      }
      if (!input.matches(".genki-filter__option input")) return;
      const details = input.closest(".genki-filter__select");
      const key = details.dataset.filter;
      if (input.checked) selected[key].add(input.value);
      else selected[key].delete(input.value);
      window.GenkiAnalytics?.filterUsed?.(key, input.value, input.checked);
      resetVisibleCount();
      writeState(selected);
      render();
    });

    sortSelect.addEventListener("change", () => {
      sortValue = sortSelect.value;
      window.GenkiAnalytics?.filterSort?.(sortValue);
      resetVisibleCount();
      writeSortState(sortValue, defaultSort);
      render();
    });

    // PERFORMANCE (26.07.): Tipp-Verzoegerung - erst wenn 180ms nicht weiter
    // getippt wurde, wird gefiltert. Vorher lief die komplette Filterung bei
    // JEDEM einzelnen Buchstaben.
    let quickSearchTimer = null;
    root.addEventListener("input", (event) => {
      const search = event.target;
      if (search.matches(".genki-filter__quick-input")) {
        clearViewState();
        filterQuery = search.value;
        visibleCount = PAGE_SIZE;
        if (quickSearchTimer) window.clearTimeout(quickSearchTimer);
        quickSearchTimer = window.setTimeout(() => {
          quickSearchTimer = null;
          writeQuickSearchState(filterQuery);
          render();
        }, 180);
        return;
      }
      if (!search.matches(".genki-filter__search")) return;
      const panel = search.closest(".genki-filter__panel");
      const key = search.closest(".genki-filter__select").dataset.filter;
      renderOptionList(panel, optionsByFilter[key] || [], selected[key], search.value);
    });

    if (quickClear) {
      quickClear.addEventListener("click", () => {
        clearViewState();
        filterQuery = "";
        if (quickInput) quickInput.value = "";
        visibleCount = PAGE_SIZE;
        writeQuickSearchState("");
        render();
        quickInput?.focus();
      });
    }

    root.querySelector(".genki-filter__reset").addEventListener("click", () => {
      window.GenkiAnalytics?.send?.("genki_filter_reset", {});
      for (const filter of FILTERS) selected[filter.key].clear();
      selected.priceMin = "";
      selected.priceMax = "";
      filterQuery = "";
      if (quickInput) quickInput.value = "";
      writeQuickSearchState("");
      root.querySelectorAll(".genki-filter__search").forEach((search) => {
        search.value = "";
      });
      root.querySelectorAll(".genki-filter__price-input").forEach((input) => {
        input.value = "";
      });
      for (const filter of FILTERS) {
        optionsByFilter[filter.key] = allOptionsByFilter[filter.key];
      }
      resetVisibleCount();
      writeState(selected);
      render();
    });

    const mobileOpen = root.querySelector(".genki-filter__mobile-open");
    const mobileClose = root.querySelector(".genki-filter__mobile-close");
    const closeMobileFilter = () => {
      root.classList.remove("is-mobile-filter-open");
      document.body.classList.remove("genki-mobile-filter-open");
      mobileOpen?.setAttribute("aria-expanded", "false");
    };
    const openMobileFilter = () => {
      root.classList.add("is-mobile-filter-open");
      document.body.classList.add("genki-mobile-filter-open");
      mobileOpen?.setAttribute("aria-expanded", "true");
    };

    mobileOpen?.addEventListener("click", openMobileFilter);
    mobileClose?.addEventListener("click", closeMobileFilter);
    if (mobileOpen && mobileOpen.parentElement !== document.body) {
      mobileOpen.dataset.genkiFilterRoot = root.id || "genki-filter";
      document.body.appendChild(mobileOpen);
    }
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && root.classList.contains("is-mobile-filter-open")) closeMobileFilter();
    });

    moreButton.addEventListener("click", () => {
      visibleCount += PAGE_SIZE;
      saveViewState();
      render({ skipOptions: true });
      if (fskVerified()) revealFsk18();
    });

    grid.addEventListener("click", (event) => {
      const link = event.target.closest("a[href]");
      if (!link || !String(link.getAttribute("href") || "").includes("/products/")) return;
      saveViewState();
    }, true);

    window.addEventListener("pagehide", saveViewState);

    root.addEventListener("click", (event) => {
      const link = event.target.closest("[data-fsk18='true']");
      if (!link || fskVerified()) return;
      event.preventDefault();
      const confirmAge = confirm("⚠ ALTERSPRÜFUNG ⚠\n\nDieser Bereich enthält Artikel für Erwachsene (FSK 18+).\n\nBist du über 18 Jahre alt?");
      if (!confirmAge) return;
      localStorage.setItem("fsk18_verified", "true");
      localStorage.setItem("fsk18_time", String(Date.now()));
      revealFsk18();
    });

    render();
    return {
      selected,
      replaceProducts(nextProducts) {
        const nextScoped = scopedProducts(nextProducts, root.dataset.collectionHandle || "", indexCollectionHandle);
        if (nextScoped === null) throw new Error("Deferred index has no verified collection scope");
        baseProducts = nextScoped;
        for (const filter of FILTERS) {
          allOptionsByFilter[filter.key] = countOptions(baseProducts, filter, null, blocklist);
        }
        render();
      },
      setLoading(message) {
        if (!loading) return;
        loading.hidden = !message;
        loading.querySelector(".genki-filter__loading-text").textContent = message || "";
      },
    };
  }

  async function boot() {
    const root = document.getElementById("GenkidamaFilterSystem");
    if (!root) return;
    try {
      const indexUrls = [root.dataset.indexUrl, root.dataset.indexFallbackUrl]
        .map((url) => String(url || "").trim())
        .filter((url, index, urls) => url && urls.indexOf(url) === index);

      const loadIndexData = async (indexUrl) => {
        const response = await genkiIndexFetch(indexUrl);
        if (!response.ok) throw new Error(`Index request failed: ${response.status}`);
        const data = await response.json();
        if (!data || !data.generated_at) throw new Error("Index metadata missing");
        let products = data.products;
        if (data.chunked === true) {
          if (!Array.isArray(data.chunks) || !data.chunks.length) throw new Error("Index chunk list missing");
          const baseUrl = new URL(indexUrl || window.location.href, window.location.href);
          products = [];
          for (const chunkName of data.chunks) {
            const chunkUrl = new URL(String(chunkName || ""), baseUrl);
            const chunkResponse = await genkiIndexFetch(chunkUrl.toString());
            if (!chunkResponse.ok) throw new Error(`Index chunk request failed: ${chunkResponse.status}`);
            const chunkData = await chunkResponse.json();
            if (chunkData.generated_at !== data.generated_at || !Array.isArray(chunkData.products)) {
              throw new Error("Mixed or invalid index chunks");
            }
            products.push(...chunkData.products);
          }
        }
        if (!Array.isArray(products) || !Number.isInteger(data.product_count) || products.length !== data.product_count) {
          throw new Error("Index product count mismatch");
        }
        const ids = new Set(products.map((product) => String(product && product.id)));
        if (ids.size !== products.length || ids.has("undefined") || ids.has("null")) {
          throw new Error("Index contains missing or duplicate product IDs");
        }
        return { ...data, products, __indexUrl: indexUrl };
      };

      let data = null;
      let lastIndexError = null;
      for (const indexUrl of indexUrls) {
        try {
          data = await loadIndexData(indexUrl);
          break;
        } catch (error) {
          lastIndexError = error;
          console.warn("Genkidama index source failed:", indexUrl, error);
        }
      }
      if (!data) throw lastIndexError || new Error("No Genkidama index URL configured");
      let blocklist = {};
      let customFilters = [];
      try {
        const blockUrl = new URL("genkidama-filter-blocklist.json", new URL(data.__indexUrl || root.dataset.indexUrl || window.location.href, window.location.href));
        const blockResponse = await genkiIndexFetch(blockUrl.toString());
        if (blockResponse.ok) {
          const blockData = await blockResponse.json();
          const raw = blockData && blockData.filters && typeof blockData.filters === "object" ? blockData.filters : {};
          customFilters = Array.isArray(blockData && blockData.custom_filters) ? blockData.custom_filters : [];
          blocklist = Object.fromEntries(Object.entries(raw).map(([key, values]) => [
            key,
            new Set((Array.isArray(values) ? values : []).map(blockedValueKey).filter(Boolean)),
          ]));
          const rawAliases = blockData && blockData.aliases && typeof blockData.aliases === "object" ? blockData.aliases : {};
          blocklist.__aliases = {};
          Object.entries(rawAliases).forEach(([filterKey, groups]) => {
            const map = new Map();
            Object.entries(groups || {}).forEach(([canonical, aliases]) => {
              const cleanCanonical = publicFilterValue(canonical);
              if (!cleanCanonical) return;
              map.set(blockedValueKey(cleanCanonical), cleanCanonical);
              (Array.isArray(aliases) ? aliases : []).forEach((alias) => {
                const cleanAlias = publicFilterValue(alias);
                if (cleanAlias) map.set(blockedValueKey(cleanAlias), cleanCanonical);
              });
            });
            blocklist.__aliases[filterKey] = map;
          });
        }
      } catch (blockError) {
        blocklist = {};
      }
      const products = Array.isArray(data.products)
        ? data.products.filter((product) => product && product.handle !== "extra-versand")
        : [];
      applyCustomFilters(customFilters);
      const segmented = data.segment === "first" && /-first\.json(?:\?|$)/.test(data.__indexUrl)
        && data.stock_counts && ["available", "preorder", "soldout"].every((key) => Number.isInteger(data.stock_counts[key]));
      if (!segmented) {
        init(root, products, blocklist, data.collection_handle || "");
        return;
      }

      const progressive = {
        stockCounts: { ...data.stock_counts },
        readyLoading: true,
        readyLoaded: false,
        soldoutLoading: false,
        soldoutLoaded: false,
        readyProducts: null,
        soldoutProducts: null,
        readyGeneration: data.generated_at,
        soldoutGeneration: null,
        error: "",
      };
      let controller;
      const syncLoading = () => {
        if (progressive.soldoutLoading && controller?.selected.stock.has("soldout")) {
          controller.setLoading("Ausverkaufte Artikel werden eingelesen …");
        } else if (progressive.readyLoading) {
          controller.setLoading("Weitere verfügbare Artikel und Vorbesteller werden eingelesen …");
        } else {
          controller.setLoading(progressive.error);
        }
      };
      const refreshProducts = () => {
        const ready = progressive.readyProducts || products;
        const soldout = progressive.soldoutProducts || [];
        // The daemon may publish a newer generation while this page is open.
        // If a product changed stock status, the newer segment wins.
        const soldoutIsNewer = progressive.soldoutGeneration
          && progressive.soldoutGeneration > progressive.readyGeneration;
        const byId = new Map();
        (soldoutIsNewer ? ready.concat(soldout) : soldout.concat(ready)).forEach((product) => {
          byId.set(String(product.id || product.handle), product);
        });
        controller.replaceProducts(Array.from(byId.values()));
        syncLoading();
      };
      const loadSegment = async (segment) => {
        const rawUrl = data.__indexUrl.replace(/-first\.json(?=\?|$)/, `-${segment}.json`);
        if (rawUrl === data.__indexUrl) throw new Error("Segment URL missing");
        const url = new URL(rawUrl, window.location.href);
        url.searchParams.set("genki_build", data.generated_at);
        const deferred = await loadIndexData(url.toString());
        if (deferred.segment !== segment
          || (deferred.collection_handle || "") !== (data.collection_handle || "")) {
          throw new Error("Index segment or collection mismatch");
        }
        return deferred;
      };
      progressive.loadReady = async () => {
        try {
          const deferred = await loadSegment("ready");
          progressive.readyProducts = deferred.products.filter((product) => product && product.handle !== "extra-versand");
          progressive.readyGeneration = deferred.generated_at;
          if (deferred.stock_counts) {
            progressive.stockCounts.available = deferred.stock_counts.available;
            progressive.stockCounts.preorder = deferred.stock_counts.preorder;
          }
          progressive.readyLoaded = true;
          progressive.error = "";
        } catch (error) {
          console.warn("Genkidama ready index failed:", error);
          progressive.error = "Weitere Artikel konnten nicht geladen werden. Bitte Seite neu laden.";
        } finally {
          progressive.readyLoading = false;
          refreshProducts();
        }
      };
      progressive.loadSoldout = () => {
        if (progressive.soldoutLoaded || progressive.soldoutLoading) return;
        progressive.soldoutLoading = true;
        syncLoading();
        loadSegment("soldout")
          .then((deferred) => {
            progressive.soldoutProducts = deferred.products.filter((product) => product && product.handle !== "extra-versand");
            progressive.soldoutGeneration = deferred.generated_at;
            if (deferred.stock_counts) progressive.stockCounts.soldout = deferred.stock_counts.soldout;
            progressive.soldoutLoaded = true;
            progressive.error = "";
          })
          .catch((error) => {
            console.warn("Genkidama soldout index failed:", error);
            progressive.error = "Ausverkaufte Artikel konnten nicht geladen werden. Bitte erneut anklicken.";
          })
          .finally(() => {
            progressive.soldoutLoading = false;
            refreshProducts();
          });
      };
      controller = init(root, products, blocklist, data.collection_handle || "", progressive);
      syncLoading();
      if (controller.selected.stock.has("soldout")) progressive.loadSoldout();
      window.requestAnimationFrame(() => window.setTimeout(progressive.loadReady, 150));
    } catch (error) {
      console.warn("Genkidama filter inactive:", error);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
